'use client';

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { billsAPI, eventsAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import '../styles/Billing.css';

function Billing() {
  const { showToast } = useToast();

  const [bills, setBills] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters & Search
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');

  // Modal / Form States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBillId, setEditingBillId] = useState(null);
  const [viewingBill, setViewingBill] = useState(null);

  // Form Fields
  const [selectedEventId, setSelectedEventId] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [eventName, setEventName] = useState('');
  const [eventDate, setEventDate] = useState(new Date().toISOString().split('T')[0]);
  const [venue, setVenue] = useState('');
  const [guestCount, setGuestCount] = useState(100);
  const [notes, setNotes] = useState('');

  // Line Items
  const [items, setItems] = useState([
    { name: 'Catering Menu / Plates', quantity: 100, rate: 350, category: 'dish' },
    { name: 'Service Charge & Staff', quantity: 1, rate: 5000, category: 'service' }
  ]);

  // Financial Calculations State
  const [discountType, setDiscountType] = useState('amount'); // 'amount' or 'percentage'
  const [discountValue, setDiscountValue] = useState(0);
  const [taxPercentage, setTaxPercentage] = useState(5); // e.g. 5% GST
  const [advancePaid, setAdvancePaid] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Add Payment Modal State
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentBill, setPaymentBill] = useState(null);
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState('upi');
  const [payNotes, setPayNotes] = useState('');
  const [savingPayment, setSavingPayment] = useState(false);

  // Fetch Bills and Events
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (statusFilter) params.status = statusFilter;
      if (dateFilter) params.startDate = dateFilter;

      const [billsRes, eventsRes] = await Promise.all([
        billsAPI.getAll(params),
        eventsAPI.getAll()
      ]);

      setBills(billsRes.data.bills || []);
      setEvents(eventsRes.data.events || []);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to load billing data', 'error');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, statusFilter, dateFilter, showToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle Event selection to auto-fill details
  const handleEventSelect = (eventId) => {
    setSelectedEventId(eventId);
    if (!eventId) return;

    const selectedEvt = events.find(e => e._id === eventId);
    if (selectedEvt) {
      setClientName(selectedEvt.clientName || '');
      setEventName(selectedEvt.eventName || '');
      if (selectedEvt.eventDate) {
        setEventDate(new Date(selectedEvt.eventDate).toISOString().split('T')[0]);
      }
      setGuestCount(selectedEvt.numberOfPeople || 100);
      setAdvancePaid(selectedEvt.advanceReceived || 0);

      // Auto-populate menu item plate rate calculation
      const plateRate = selectedEvt.numberOfPeople > 0
        ? Math.round(selectedEvt.totalBill / selectedEvt.numberOfPeople)
        : 350;

      setItems([
        {
          name: `${selectedEvt.eventName} Catering Menu`,
          quantity: selectedEvt.numberOfPeople || 100,
          rate: plateRate || 350,
          category: 'dish'
        }
      ]);
    }
  };

  // Live Calculations on Frontend
  const liveTotals = useMemo(() => {
    let subtotal = 0;
    const computedItems = items.map(item => {
      const q = Number(item.quantity) || 0;
      const r = Number(item.rate) || 0;
      const amount = q * r;
      subtotal += amount;
      return { ...item, amount };
    });

    let discountAmount = 0;
    const discVal = Number(discountValue) || 0;
    if (discountType === 'percentage') {
      discountAmount = (subtotal * discVal) / 100;
    } else {
      discountAmount = discVal;
    }
    discountAmount = Math.min(subtotal, Math.max(0, discountAmount));
    const taxable = Math.max(0, subtotal - discountAmount);

    const taxPct = Number(taxPercentage) || 0;
    const taxAmount = (taxable * taxPct) / 100;
    const grandTotal = Math.round((taxable + taxAmount) * 100) / 100;

    const adv = Number(advancePaid) || 0;
    const balanceDue = Math.max(0, Math.round((grandTotal - adv) * 100) / 100);

    let paymentStatus = 'unpaid';
    if (adv >= grandTotal && grandTotal > 0) {
      paymentStatus = 'paid';
    } else if (adv > 0) {
      paymentStatus = 'partly_paid';
    }

    return {
      computedItems,
      subtotal,
      discountAmount,
      taxAmount,
      grandTotal,
      balanceDue,
      paymentStatus
    };
  }, [items, discountType, discountValue, taxPercentage, advancePaid]);

  // Line Item Handlers
  const handleItemChange = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;
    setItems(updated);
  };

  const handleAddItem = (category = 'dish', nameHint = 'New Dish / Item') => {
    setItems([
      ...items,
      { name: nameHint, quantity: 1, rate: 0, category }
    ]);
  };

  const handleRemoveItem = (index) => {
    if (items.length === 1) {
      showToast('Bill must have at least one line item', 'warning');
      return;
    }
    setItems(items.filter((_, i) => i !== index));
  };

  // Open Form Modal for New or Edit
  const handleOpenForm = (billToEdit = null) => {
    if (billToEdit) {
      setEditingBillId(billToEdit._id);
      setSelectedEventId(billToEdit.event?._id || '');
      setClientName(billToEdit.clientName || '');
      setClientPhone(billToEdit.clientPhone || '');
      setEventName(billToEdit.eventName || '');
      setEventDate(new Date(billToEdit.eventDate).toISOString().split('T')[0]);
      setVenue(billToEdit.venue || '');
      setGuestCount(billToEdit.guestCount || 0);
      setItems(billToEdit.items && billToEdit.items.length > 0 ? billToEdit.items : []);
      setDiscountType(billToEdit.discountType || 'amount');
      setDiscountValue(billToEdit.discountValue || 0);
      setTaxPercentage(billToEdit.taxPercentage || 0);
      setAdvancePaid(billToEdit.advancePaid || 0);
      setNotes(billToEdit.notes || '');
    } else {
      setEditingBillId(null);
      setSelectedEventId('');
      setClientName('');
      setClientPhone('');
      setEventName('');
      setEventDate(new Date().toISOString().split('T')[0]);
      setVenue('');
      setGuestCount(100);
      setItems([
        { name: 'Plates / Catering Menu', quantity: 100, rate: 350, category: 'dish' },
        { name: 'Transport & Delivery', quantity: 1, rate: 2000, category: 'transport' }
      ]);
      setDiscountType('amount');
      setDiscountValue(0);
      setTaxPercentage(5);
      setAdvancePaid(0);
      setNotes('');
    }
    setIsModalOpen(true);
  };

  // Submit Bill Form
  const handleSubmitBill = async (e) => {
    e.preventDefault();
    if (!clientName.trim() || !eventName.trim()) {
      showToast('Client Name and Event Name are required', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        event: selectedEventId || null,
        clientName,
        clientPhone,
        eventName,
        eventDate,
        venue,
        guestCount: Number(guestCount),
        items,
        discountType,
        discountValue: Number(discountValue),
        taxPercentage: Number(taxPercentage),
        advancePaid: Number(advancePaid),
        notes
      };

      if (editingBillId) {
        await billsAPI.update(editingBillId, payload);
        showToast('Bill updated successfully!', 'success');
      } else {
        await billsAPI.create(payload);
        showToast('Bill created successfully!', 'success');
      }

      setIsModalOpen(false);
      fetchData();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save bill', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Delete Bill
  const handleDeleteBill = async (id, billNo) => {
    if (!window.confirm(`Are you sure you want to delete invoice ${billNo}?`)) return;

    try {
      await billsAPI.delete(id);
      showToast(`Bill ${billNo} deleted`, 'success');
      if (viewingBill?._id === id) setViewingBill(null);
      fetchData();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete bill', 'error');
    }
  };

  // Add Payment Handler
  const handleOpenPaymentModal = (bill) => {
    setPaymentBill(bill);
    setPayAmount(bill.balanceDue > 0 ? bill.balanceDue : '');
    setPayMethod('upi');
    setPayNotes('');
    setIsPaymentModalOpen(true);
  };

  const handleSavePayment = async (e) => {
    e.preventDefault();
    if (!payAmount || Number(payAmount) <= 0) {
      showToast('Enter a valid payment amount', 'error');
      return;
    }

    setSavingPayment(true);
    try {
      const res = await billsAPI.addPayment(paymentBill._id, {
        amount: Number(payAmount),
        paymentMethod: payMethod,
        notes: payNotes
      });

      showToast('Payment recorded successfully!', 'success');
      setIsPaymentModalOpen(false);
      if (viewingBill?._id === paymentBill._id) {
        setViewingBill(res.data.bill);
      }
      fetchData();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to record payment', 'error');
    } finally {
      setSavingPayment(false);
    }
  };

  // Copy Plain Text Bill Summary
  const handleCopyBill = (bill) => {
    const formattedItems = (bill.items || [])
      .map(i => `  • ${i.name} (${i.quantity} x ₹${i.rate}) = ₹${i.amount.toLocaleString('en-IN')}`)
      .join('\n');

    const text = `
========================================
INVOICE: ${bill.billNumber}
Vegetarian Catering Management System
========================================
Client: ${bill.clientName} ${bill.clientPhone ? `(${bill.clientPhone})` : ''}
Event: ${bill.eventName}
Date: ${new Date(bill.eventDate).toLocaleDateString()}
${bill.venue ? `Venue: ${bill.venue}` : ''}

ITEMIZED BREAKDOWN:
${formattedItems}
----------------------------------------
Subtotal: ₹${bill.subtotal.toLocaleString('en-IN')}
${bill.discountAmount > 0 ? `Discount: -₹${bill.discountAmount.toLocaleString('en-IN')}\n` : ''}${bill.taxAmount > 0 ? `GST Tax (${bill.taxPercentage}%): +₹${bill.taxAmount.toLocaleString('en-IN')}\n` : ''}----------------------------------------
GRAND TOTAL: ₹${bill.grandTotal.toLocaleString('en-IN')}
Advance Paid: ₹${bill.advancePaid.toLocaleString('en-IN')}
BALANCE DUE: ₹${bill.balanceDue.toLocaleString('en-IN')}
Payment Status: ${bill.paymentStatus.toUpperCase()}
========================================
Thank you for your business!
`;

    navigator.clipboard.writeText(text.trim());
    showToast('Bill plain-text summary copied to clipboard!', 'success');
  };

  // Share on WhatsApp
  const handleShareWhatsApp = (bill) => {
    const message = `Hello *${bill.clientName}*,\nHere is the invoice summary for *${bill.eventName}*:\n\n*Invoice No:* ${bill.billNumber}\n*Event Date:* ${new Date(bill.eventDate).toLocaleDateString()}\n*Grand Total:* ₹${bill.grandTotal.toLocaleString('en-IN')}\n*Advance Paid:* ₹${bill.advancePaid.toLocaleString('en-IN')}\n*Balance Due:* ₹${bill.balanceDue.toLocaleString('en-IN')}\n*Payment Status:* ${bill.paymentStatus.toUpperCase()}\n\nThank you for choosing our Catering Service!`;

    const encoded = encodeURIComponent(message);
    const phone = bill.clientPhone ? bill.clientPhone.replace(/\D/g, '') : '';
    const url = phone ? `https://api.whatsapp.com/send?phone=${phone}&text=${encoded}` : `https://api.whatsapp.com/send?text=${encoded}`;
    window.open(url, '_blank');
  };

  // Print Bill PDF
  const handlePrintPDF = (bill) => {
    setViewingBill(bill);
    setTimeout(() => {
      window.print();
    }, 300);
  };

  // Status Badge Helper
  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <span className="status-badge status-paid">PAID</span>;
      case 'partly_paid':
        return <span className="status-badge status-partly">PARTLY PAID</span>;
      default:
        return <span className="status-badge status-unpaid">UNPAID</span>;
    }
  };

  // Overall Totals KPI Metrics
  const metrics = useMemo(() => {
    const totalBilled = bills.reduce((sum, b) => sum + (b.grandTotal || 0), 0);
    const totalCollected = bills.reduce((sum, b) => sum + (b.advancePaid || 0), 0);
    const totalDue = bills.reduce((sum, b) => sum + (b.balanceDue || 0), 0);
    return { totalBilled, totalCollected, totalDue };
  }, [bills]);

  return (
    <div className="billing-page-container">
      {/* Top Header */}
      <div className="billing-header">
        <div>
          <h1>Billing & Invoicing System</h1>
          <p className="billing-subtitle">
            Generate, track, and manage event invoices, extra charges, taxes, and customer payments.
          </p>
        </div>
        <button onClick={() => handleOpenForm(null)} className="create-bill-btn">
          + Create New Bill
        </button>
      </div>

      {/* KPI Overview Grid */}
      <div className="billing-kpi-grid">
        <div className="billing-kpi-card blue">
          <div className="kpi-label">Total Invoiced Amount</div>
          <div className="kpi-val">₹{metrics.totalBilled.toLocaleString('en-IN')}</div>
        </div>
        <div className="billing-kpi-card green">
          <div className="kpi-label">Total Payments Received</div>
          <div className="kpi-val">₹{metrics.totalCollected.toLocaleString('en-IN')}</div>
        </div>
        <div className="billing-kpi-card red">
          <div className="kpi-label">Total Outstanding Balance</div>
          <div className="kpi-val">₹{metrics.totalDue.toLocaleString('en-IN')}</div>
        </div>
      </div>

      {/* Search & Filter Toolbar */}
      <div className="billing-toolbar">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by Bill #, Client Name, Event Name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="billing-search-input"
          />
        </div>

        <div className="filters-row">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="billing-filter-select"
          >
            <option value="">All Payment Statuses</option>
            <option value="paid">Paid</option>
            <option value="partly_paid">Partly Paid</option>
            <option value="unpaid">Unpaid</option>
          </select>

          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="billing-filter-date"
          />

          {(searchQuery || statusFilter || dateFilter) && (
            <button
              onClick={() => { setSearchQuery(''); setStatusFilter(''); setDateFilter(''); }}
              className="clear-filter-btn"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Bills Table */}
      <div className="bills-table-card">
        <h2>Invoices & Bills History ({bills.length})</h2>

        {loading ? (
          <div className="loading-state">Loading billing records...</div>
        ) : bills.length === 0 ? (
          <div className="no-bills-state">
            <p>No invoices found. Click <strong>"+ Create New Bill"</strong> to generate your first invoice!</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="bills-table">
              <thead>
                <tr>
                  <th>Bill #</th>
                  <th>Client & Event</th>
                  <th>Event Date</th>
                  <th>Grand Total</th>
                  <th>Paid</th>
                  <th>Balance Due</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {bills.map((bill) => (
                  <tr key={bill._id}>
                    <td className="bill-num-cell">
                      <strong>{bill.billNumber}</strong>
                    </td>
                    <td>
                      <div className="client-info">
                        <strong>{bill.clientName}</strong>
                        <span className="event-sub">{bill.eventName}</span>
                      </div>
                    </td>
                    <td>{new Date(bill.eventDate).toLocaleDateString()}</td>
                    <td className="amount-bold">₹{bill.grandTotal.toLocaleString('en-IN')}</td>
                    <td className="paid-val">₹{bill.advancePaid.toLocaleString('en-IN')}</td>
                    <td className={bill.balanceDue > 0 ? 'due-bold' : 'zero-due'}>
                      ₹{bill.balanceDue.toLocaleString('en-IN')}
                    </td>
                    <td>{getStatusBadge(bill.paymentStatus)}</td>
                    <td>
                      <div className="actions-cell">
                        <button
                          onClick={() => setViewingBill(bill)}
                          className="action-btn view-btn"
                          title="View Invoice & Print"
                        >
                          👁️ View / Print
                        </button>
                        <button
                          onClick={() => handleCopyBill(bill)}
                          className="action-btn copy-btn"
                          title="Copy Plain Text Bill"
                        >
                          📋 Copy
                        </button>
                        <button
                          onClick={() => handleShareWhatsApp(bill)}
                          className="action-btn wa-btn"
                          title="Share on WhatsApp"
                        >
                          📲 WhatsApp
                        </button>
                        <button
                          onClick={() => handleOpenPaymentModal(bill)}
                          className="action-btn pay-btn"
                          title="Record Payment"
                        >
                          💳 + Payment
                        </button>
                        <button
                          onClick={() => handleOpenForm(bill)}
                          className="action-btn edit-btn"
                          title="Edit Bill"
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={() => handleDeleteBill(bill._id, bill.billNumber)}
                          className="action-btn del-btn"
                          title="Delete Bill"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* CREATE / EDIT BILL MODAL */}
      {isModalOpen && (
        <div className="modal-backdrop">
          <div className="modal-card wide-modal">
            <div className="modal-header">
              <h2>{editingBillId ? `Edit Invoice (${editingBillId})` : 'Create New Event Invoice'}</h2>
              <button onClick={() => setIsModalOpen(false)} className="modal-close-icon">&times;</button>
            </div>

            <form onSubmit={handleSubmitBill} className="bill-form">
              {/* Event Auto-Fill Selector */}
              {!editingBillId && (
                <div className="form-section autofill-section">
                  <label className="section-label">⚡ Auto-fill from Existing Event (Optional):</label>
                  <select
                    value={selectedEventId}
                    onChange={(e) => handleEventSelect(e.target.value)}
                    className="billing-select"
                  >
                    <option value="">-- Choose an Event to Auto-fill Client & Date --</option>
                    {events.map((e) => (
                      <option key={e._id} value={e._id}>
                        {e.eventName} - {e.clientName} ({new Date(e.eventDate).toLocaleDateString()})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Client & Event Info Fields */}
              <div className="form-row-3">
                <div className="form-group">
                  <label>Client Name *</label>
                  <input
                    type="text"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    placeholder="e.g. Rahul Sharma"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Client Phone</label>
                  <input
                    type="text"
                    value={clientPhone}
                    onChange={(e) => setClientPhone(e.target.value)}
                    placeholder="e.g. 9876543210"
                  />
                </div>

                <div className="form-group">
                  <label>Event Name *</label>
                  <input
                    type="text"
                    value={eventName}
                    onChange={(e) => setEventName(e.target.value)}
                    placeholder="e.g. Grand Wedding Reception"
                    required
                  />
                </div>
              </div>

              <div className="form-row-3">
                <div className="form-group">
                  <label>Event Date *</label>
                  <input
                    type="date"
                    value={eventDate}
                    onChange={(e) => setEventDate(e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Venue / Location</label>
                  <input
                    type="text"
                    value={venue}
                    onChange={(e) => setVenue(e.target.value)}
                    placeholder="e.g. Royal Palace Hall"
                  />
                </div>

                <div className="form-group">
                  <label>Number of Guests (Plates)</label>
                  <input
                    type="number"
                    value={guestCount}
                    onChange={(e) => setGuestCount(e.target.value)}
                    min="0"
                  />
                </div>
              </div>

              {/* Line Items Section */}
              <div className="items-section">
                <div className="items-header">
                  <h3>Line Items & Charges</h3>
                  <div className="quick-add-buttons">
                    <button
                      type="button"
                      onClick={() => handleAddItem('dish', 'Special Thali / Plate')}
                      className="add-item-chip"
                    >
                      + Dish Item
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAddItem('service', 'Service Charge')}
                      className="add-item-chip"
                    >
                      + Service Charge
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAddItem('transport', 'Transport & Delivery')}
                      className="add-item-chip"
                    >
                      + Transport
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAddItem('decoration', 'Decoration & Lighting')}
                      className="add-item-chip"
                    >
                      + Decoration
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAddItem('staff', 'Extra Waiters / Staff')}
                      className="add-item-chip"
                    >
                      + Staff
                    </button>
                  </div>
                </div>

                <div className="items-table-wrapper">
                  <table className="items-editor-table">
                    <thead>
                      <tr>
                        <th style={{ width: '40%' }}>Item / Dish Name</th>
                        <th style={{ width: '15%' }}>Qty / Plates</th>
                        <th style={{ width: '20%' }}>Rate per Unit (₹)</th>
                        <th style={{ width: '20%' }}>Amount (₹)</th>
                        <th style={{ width: '5%' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, index) => (
                        <tr key={index}>
                          <td>
                            <input
                              type="text"
                              value={item.name}
                              onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                              placeholder="Item description"
                              className="item-input"
                              required
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                              min="0"
                              className="item-input"
                              required
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              value={item.rate}
                              onChange={(e) => handleItemChange(index, 'rate', e.target.value)}
                              min="0"
                              className="item-input"
                              required
                            />
                          </td>
                          <td className="item-amount-col">
                            ₹{((Number(item.quantity) || 0) * (Number(item.rate) || 0)).toLocaleString('en-IN')}
                          </td>
                          <td>
                            <button
                              type="button"
                              onClick={() => handleRemoveItem(index)}
                              className="remove-row-btn"
                              title="Remove Item"
                            >
                              ✕
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Totals & Calculations Section */}
              <div className="financials-calculator-grid">
                <div className="notes-box-col">
                  <label>Additional Notes / Terms:</label>
                  <textarea
                    rows="4"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="e.g. 50% advance received, balance payable on event date..."
                    className="notes-textarea"
                  />
                </div>

                <div className="totals-summary-col">
                  <div className="summary-line">
                    <span>Subtotal:</span>
                    <strong>₹{liveTotals.subtotal.toLocaleString('en-IN')}</strong>
                  </div>

                  <div className="summary-line-input">
                    <label>Discount:</label>
                    <div className="discount-inputs-group">
                      <select
                        value={discountType}
                        onChange={(e) => setDiscountType(e.target.value)}
                        className="disc-type-select"
                      >
                        <option value="amount">Fixed ₹</option>
                        <option value="percentage">% Percent</option>
                      </select>
                      <input
                        type="number"
                        value={discountValue}
                        onChange={(e) => setDiscountValue(e.target.value)}
                        min="0"
                        className="disc-val-input"
                      />
                    </div>
                    <span className="summary-calc-deduct">-₹{liveTotals.discountAmount.toLocaleString('en-IN')}</span>
                  </div>

                  <div className="summary-line-input">
                    <label>GST / Tax (%):</label>
                    <input
                      type="number"
                      value={taxPercentage}
                      onChange={(e) => setTaxPercentage(e.target.value)}
                      min="0"
                      max="100"
                      className="tax-pct-input"
                    />
                    <span className="summary-calc-add">+₹{liveTotals.taxAmount.toLocaleString('en-IN')}</span>
                  </div>

                  <div className="summary-line grand-total-line">
                    <span>GRAND TOTAL:</span>
                    <span>₹{liveTotals.grandTotal.toLocaleString('en-IN')}</span>
                  </div>

                  <div className="summary-line-input">
                    <label>Advance Received (₹):</label>
                    <input
                      type="number"
                      value={advancePaid}
                      onChange={(e) => setAdvancePaid(e.target.value)}
                      min="0"
                      className="advance-input-field"
                    />
                  </div>

                  <div className="summary-line balance-line">
                    <span>BALANCE DUE:</span>
                    <span className={liveTotals.balanceDue > 0 ? 'due-text' : 'paid-text'}>
                      ₹{liveTotals.balanceDue.toLocaleString('en-IN')}
                    </span>
                  </div>

                  <div className="summary-status-pill">
                    Status: {getStatusBadge(liveTotals.paymentStatus)}
                  </div>
                </div>
              </div>

              {/* Form Buttons */}
              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="cancel-modal-btn"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="save-modal-btn"
                >
                  {submitting ? 'Saving Invoice...' : editingBillId ? 'Update Bill' : 'Generate & Save Bill'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* VIEW & PRINT BILL MODAL */}
      {viewingBill && (
        <div className="modal-backdrop">
          <div className="modal-card print-preview-modal">
            <div className="modal-header no-print">
              <h2>Invoice Preview & Print ({viewingBill.billNumber})</h2>
              <div className="preview-top-actions">
                <button onClick={() => handlePrintPDF(viewingBill)} className="print-trigger-btn">
                  🖨️ Print / Save PDF
                </button>
                <button onClick={() => handleCopyBill(viewingBill)} className="copy-trigger-btn">
                  📋 Copy Text
                </button>
                <button onClick={() => handleShareWhatsApp(viewingBill)} className="wa-trigger-btn">
                  📲 WhatsApp
                </button>
                <button onClick={() => setViewingBill(null)} className="modal-close-icon">&times;</button>
              </div>
            </div>

            {/* Printable Area */}
            <div className="printable-invoice-container" id="printable-invoice">
              <div className="invoice-header">
                <div className="company-details">
                  <h1 className="company-name">Vegetarian Catering Services</h1>
                  <p>Pure Veg Gourmet Catering & Event Management</p>
                  <p>Contact: +91 98765 43210 | Email: info@catering.com</p>
                </div>
                <div className="invoice-badge-box">
                  <h2 className="inv-title">TAX INVOICE</h2>
                  <div className="inv-number">{viewingBill.billNumber}</div>
                  <div className="inv-date">Date: {new Date(viewingBill.eventDate).toLocaleDateString()}</div>
                  <div className="inv-status-tag">Status: {viewingBill.paymentStatus.toUpperCase()}</div>
                </div>
              </div>

              <div className="bill-to-grid">
                <div className="bill-to-col">
                  <h4>BILL TO:</h4>
                  <div className="client-name">{viewingBill.clientName}</div>
                  {viewingBill.clientPhone && <div>Phone: {viewingBill.clientPhone}</div>}
                </div>
                <div className="event-info-col">
                  <h4>EVENT DETAILS:</h4>
                  <div><strong>Event:</strong> {viewingBill.eventName}</div>
                  {viewingBill.venue && <div><strong>Venue:</strong> {viewingBill.venue}</div>}
                  {viewingBill.guestCount > 0 && <div><strong>Guest Count:</strong> {viewingBill.guestCount} Plates</div>}
                </div>
              </div>

              {/* Items Table */}
              <table className="print-items-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Item / Service Description</th>
                    <th>Qty</th>
                    <th>Rate (₹)</th>
                    <th>Amount (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {(viewingBill.items || []).map((item, idx) => (
                    <tr key={idx}>
                      <td>{idx + 1}</td>
                      <td>{item.name}</td>
                      <td>{item.quantity}</td>
                      <td>₹{item.rate.toLocaleString('en-IN')}</td>
                      <td>₹{item.amount.toLocaleString('en-IN')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Print Totals Summary */}
              <div className="print-totals-grid">
                <div className="terms-col">
                  {viewingBill.notes && (
                    <div className="notes-print-box">
                      <h5>Notes & Terms:</h5>
                      <p>{viewingBill.notes}</p>
                    </div>
                  )}

                  {viewingBill.payments && viewingBill.payments.length > 0 && (
                    <div className="payments-history-box">
                      <h5>Payment History:</h5>
                      <ul>
                        {viewingBill.payments.map((p, i) => (
                          <li key={i}>
                            {new Date(p.paymentDate).toLocaleDateString()} — ₹{p.amount.toLocaleString('en-IN')} ({p.paymentMethod.toUpperCase()}) {p.notes ? `- ${p.notes}` : ''}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <div className="totals-print-box">
                  <div className="p-total-line">
                    <span>Subtotal:</span>
                    <span>₹{viewingBill.subtotal.toLocaleString('en-IN')}</span>
                  </div>

                  {viewingBill.discountAmount > 0 && (
                    <div className="p-total-line">
                      <span>Discount ({viewingBill.discountType === 'percentage' ? `${viewingBill.discountValue}%` : 'Fixed'}):</span>
                      <span>-₹{viewingBill.discountAmount.toLocaleString('en-IN')}</span>
                    </div>
                  )}

                  {viewingBill.taxAmount > 0 && (
                    <div className="p-total-line">
                      <span>GST Tax ({viewingBill.taxPercentage}%):</span>
                      <span>+₹{viewingBill.taxAmount.toLocaleString('en-IN')}</span>
                    </div>
                  )}

                  <div className="p-total-line p-grand-total">
                    <span>Grand Total:</span>
                    <span>₹{viewingBill.grandTotal.toLocaleString('en-IN')}</span>
                  </div>

                  <div className="p-total-line">
                    <span>Advance Received:</span>
                    <span>₹{viewingBill.advancePaid.toLocaleString('en-IN')}</span>
                  </div>

                  <div className="p-total-line p-balance-line">
                    <span>Balance Due:</span>
                    <span>₹{viewingBill.balanceDue.toLocaleString('en-IN')}</span>
                  </div>
                </div>
              </div>

              <div className="print-footer">
                <p>This is a computer generated invoice. Thank you for your business!</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* RECORD PAYMENT MODAL */}
      {isPaymentModalOpen && paymentBill && (
        <div className="modal-backdrop">
          <div className="modal-card">
            <div className="modal-header">
              <h3>Record Payment for Invoice {paymentBill.billNumber}</h3>
              <button onClick={() => setIsPaymentModalOpen(false)} className="modal-close-icon">&times;</button>
            </div>

            <form onSubmit={handleSavePayment} className="payment-form">
              <div className="pay-summary-box">
                <div>Client: <strong>{paymentBill.clientName}</strong></div>
                <div>Grand Total: <strong>₹{paymentBill.grandTotal.toLocaleString('en-IN')}</strong></div>
                <div>Already Paid: <strong className="green-text">₹{paymentBill.advancePaid.toLocaleString('en-IN')}</strong></div>
                <div>Balance Due: <strong className="red-text">₹{paymentBill.balanceDue.toLocaleString('en-IN')}</strong></div>
              </div>

              <div className="form-group">
                <label>Payment Amount (₹) *</label>
                <input
                  type="number"
                  value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value)}
                  placeholder="Enter amount"
                  min="1"
                  required
                />
              </div>

              <div className="form-group">
                <label>Payment Method</label>
                <select
                  value={payMethod}
                  onChange={(e) => setPayMethod(e.target.value)}
                  className="billing-select"
                >
                  <option value="upi">UPI / GPay / PhonePe</option>
                  <option value="cash">Cash</option>
                  <option value="bank_transfer">Bank Transfer / NEFT</option>
                  <option value="cheque">Cheque</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label>Notes / Reference (Optional)</label>
                <input
                  type="text"
                  value={payNotes}
                  onChange={(e) => setPayNotes(e.target.value)}
                  placeholder="e.g. Trans ID 123456"
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => setIsPaymentModalOpen(false)}
                  className="cancel-modal-btn"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingPayment}
                  className="save-modal-btn"
                >
                  {savingPayment ? 'Saving Payment...' : 'Record Payment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Billing;
